import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, Clock, Wallet, Users } from "lucide-react";
import { api } from "@/lib/api";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import FocusPull from "@/components/site/FocusPull";

function Corner({ className }) {
  return <span className={`absolute w-6 h-6 border-lima-blue ${className}`} aria-hidden="true" />;
}

export default function CoursePage() {
  const { slug } = useParams();
  const [course, setCourse] = useState(undefined);

  useEffect(() => {
    setCourse(undefined);
    api.get(`/courses/${slug}`).then((r) => setCourse(r.data)).catch(() => setCourse(null));
  }, [slug]);

  return (
    <div className="bg-lima-ink text-lima-paper min-h-screen font-body" data-testid="course-page">
      <Navbar />
      {course === undefined ? (
        <div className="min-h-screen grid place-items-center font-mono2 text-xs tracking-[0.3em] text-white/50" data-testid="course-loading">
          LOADING…
        </div>
      ) : course === null ? (
        <div className="min-h-screen flex flex-col items-start justify-center px-5 md:px-10" data-testid="course-not-found">
          <p className="font-mono2 text-xs tracking-[0.25em] text-lima-red mb-4">[ 404 — SLATE NOT FOUND ]</p>
          <h1 className="font-display font-bold text-4xl md:text-6xl tracking-tight">
            This program doesn't <span className="font-serifit font-normal text-lima-blue">exist (yet).</span>
          </h1>
          <Link to="/" data-testid="course-back-home-link" className="mt-8 flex items-center gap-2 font-mono2 text-xs tracking-[0.2em] text-white/70 hover:text-white">
            <ArrowLeft size={14} /> BACK TO ALL PROGRAMS
          </Link>
        </div>
      ) : (
        <>
          <section className="pt-28 md:pt-36 px-5 md:px-10">
            <Link to="/" data-testid="course-back-link" className="inline-flex items-center gap-2 font-mono2 text-xs tracking-[0.25em] text-white/50 hover:text-white transition-colors duration-200 mb-10">
              <ArrowLeft size={14} /> ALL PROGRAMS
            </Link>
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
              <div className="lg:col-span-7">
                <FocusPull amount={18} y={20}>
                  <p className="font-mono2 text-[10px] md:text-xs tracking-[0.25em] text-lima-red mb-5">
                  </p>
                  <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[0.98]" data-testid="course-title">
                    {course.title}
                  </h1>
                  {course.tagline && (
                    <p className="mt-4 font-serifit text-2xl md:text-3xl text-lima-blue leading-snug">{course.tagline}</p>
                  )}
                  <p className="mt-6 max-w-xl text-white/60 text-sm md:text-base leading-relaxed">{course.description}</p>
                </FocusPull>
              </div>
              <motion.div
                initial={{ opacity: 0, scale: 0.96, filter: "blur(16px)" }}
                animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                transition={{ delay: 0.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="lg:col-span-5 relative"
              >
                <Corner className="-top-2 -left-2 border-t-2 border-l-2" />
                <Corner className="-top-2 -right-2 border-t-2 border-r-2" />
                <Corner className="-bottom-2 -left-2 border-b-2 border-l-2" />
                <Corner className="-bottom-2 -right-2 border-b-2 border-r-2" />
                <div className="border border-white/10 overflow-hidden">
                  <img src={course.image} alt={course.title} className="w-full h-64 md:h-80 object-cover saturate-[.7]" data-testid="course-image" />
                </div>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 border-y border-white/10 divide-y sm:divide-y-0 sm:divide-x divide-white/10 mt-14" data-testid="course-meta-strip">
              {[
                { icon: Clock, label: "DURATION", value: course.duration || "—" },
                { icon: Wallet, label: "FEE", value: course.fee || "Fee on request", note: course.fee_note },
                { icon: Users, label: "BATCH", value: "10–15 students" },
              ].map((m) => (
                <div key={m.label} className="p-6 md:p-8 flex items-start gap-4">
                  <m.icon size={17} className="text-lima-blue mt-1 shrink-0" />
                  <div>
                    <p className="font-mono2 text-[11px] tracking-[0.25em] text-white/40">{m.label}</p>
                    <p className="mt-2 font-display font-bold text-xl md:text-2xl tracking-tight">{m.value}</p>
                    {m.note && <p className="mt-1 text-xs text-white/40">{m.note}</p>}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {course.includes?.length > 0 && (
            <section className="px-5 md:px-10 py-14 md:py-28" data-testid="course-includes-section">
              <FocusPull>
                <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight mb-12">
                  Everything <span className="font-serifit font-normal text-lima-blue">in the program.</span>
                </h2>
              </FocusPull>
              <div className="grid sm:grid-cols-2 border-t border-l border-white/10">
                {course.includes.map((inc, i) => (
                  <FocusPull key={i} delay={i * 0.05} amount={8} y={16} className="border-b border-r border-white/10">
                    <div className="p-6 md:p-8 flex items-start gap-4 h-full">
                      <Check size={16} className="text-lima-blue shrink-0 mt-1" />
                      <p className="text-sm md:text-base text-white/75 leading-relaxed">{inc}</p>
                    </div>
                  </FocusPull>
                ))}
              </div>
            </section>
          )}

          {course.modules?.length > 0 && (
            <section className="px-5 md:px-10 pb-14 md:pb-28" data-testid="course-curriculum-section">
              <FocusPull>
                <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight mb-12">
                  The <span className="font-serifit font-normal text-lima-blue">curriculum.</span>
                </h2>
              </FocusPull>
              <div className="border-t border-white/10">
                {course.modules.map((mod, i) => (
                  <FocusPull key={i} delay={i * 0.05} amount={10} y={24}>
                    <div className="border-b border-white/10 py-8 md:py-10 grid md:grid-cols-12 gap-5" data-testid={`course-module-${i}`}>
                      <div className="md:col-span-2">
                        <span className="font-serifd text-4xl md:text-5xl text-lima-blue leading-none">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <div className="md:col-span-5">
                        <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight">{mod.title}</h3>
                      </div>
                      <div className="md:col-span-5 flex flex-wrap gap-2 content-start">
                        {mod.topics.map((t, j) => (
                          <span key={j} className="font-mono2 text-[11px] tracking-[0.1em] border-b border-white/20 text-white/60 px-1 py-1.5">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  </FocusPull>
                ))}
              </div>
            </section>
          )}

          <section className="bg-lima-navy px-5 md:px-10 py-12 md:py-24">
            <FocusPull className="flex flex-col md:flex-row md:items-center justify-between gap-8">
              <div>
                <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight">
                  Ready to roll on <span className="font-serifit font-normal text-lima-blue">{course.title}?</span>
                </h2>
                <p className="mt-3 text-sm text-white/50">Batches are capped. Tell us you're interested and we'll hold a seat conversation.</p>
              </div>
              <Link
                to={`/?enquire=${course.slug}`}
                data-testid="course-enquire-cta"
                className="shrink-0 bg-lima-blue hover:bg-lima-bluehover text-white font-mono2 text-xs font-bold tracking-[0.2em] px-8 py-4 transition-colors duration-200 flex items-center gap-3"
              >
                ENQUIRE ABOUT THIS PROGRAM <ArrowUpRight size={15} />
              </Link>
            </FocusPull>
          </section>
        </>
      )}
      <Footer />
    </div>
  );
}
